/**
 * The sign-up questions' words and keys, exactly as CONTRACT §5 has them.
 *
 * The keys are what the API stores and what admins export, so they never
 * change once people have answered; a label can be reworded freely. The Sept 19
 * pass reworded almost every label and added a question — read the notes on
 * `policy-government-advocacy` and `nyc-local` before tidying anything here.
 */

export const TERMS_VERSION = 'pilot-2026-09';

export const MAX_PICKS = 3;

/** Picking this one asks for a few words of the user's own. */
export const SOMETHING_ELSE = 'something-else';

export interface Option {
  key: string;
  /** Drawn at the left of the row, and again on the account page's chips. */
  emoji: string;
  label: string;
  description?: string;
}

export const DESCRIPTORS: Option[] = [
  {
    key: 'government-public-sector',
    emoji: '🏛️',
    label: 'Govt / Public Sector Professional',
    description: 'I work for a government agency',
  },
  {
    /**
     * Still `policy-government-advocacy`, and it stays that way. Sept 19 split
     * the old "Policy / Govt / Advocacy" row in two and this half kept the
     * original key, because it is what every answer given before then is
     * stored as. Renaming it to match the new label would orphan all of them
     * and break `docs/admin/onboarding-export.sql`.
     */
    key: 'policy-government-advocacy',
    emoji: '🪧',
    label: 'Policy / Advocacy / NGO Worker',
    description: 'I work on campaigns, media, or organizing',
  },
  {
    key: 'climate-professional',
    emoji: '💼',
    label: 'Corporate Professional',
    description: 'I work in climate at a company',
  },
  {
    key: 'founder-operator',
    emoji: '🚀',
    label: 'Founder / Operator',
    description: 'I’m building a company, product, or project',
  },
  {
    key: 'investor-funder',
    emoji: '💰',
    label: 'Investor / Funder',
    description: 'I’m in VC, growth, philanthropy, family office',
  },
  {
    key: 'student-early-career',
    emoji: '🧑‍🎓',
    label: 'Student / New to Climate',
    description: 'I’m studying, or just starting out in climate',
  },
  { key: SOMETHING_ELSE, emoji: '💬', label: 'Something else…', description: 'Write your own answer!' },
];

/**
 * Answers nobody can give any more, but that accounts still hold.
 *
 * `nyc-local` left this question on Sept 19 — the travel question below asks it
 * properly — and an account that answered it before then would otherwise show a
 * bare key on the account page, or quietly lose the answer.
 */
export const RETIRED_DESCRIPTORS: Record<string, Option> = {
  'nyc-local': { key: 'nyc-local', emoji: '🗽', label: 'NYC local' },
};

/**
 * Where you are based (Sept 19; reworded Sept 20). One answer only.
 *
 * Today's API rejects the field outright — its schema is strict — so the
 * answer is kept in the browser and sent hopefully; see `OnboardingPage`.
 */
export const ORIGINS: Option[] = [
  { key: 'nyc-local', emoji: '🗽', label: 'NYC local' },
  { key: 'us-canada', emoji: '🇺🇸🇨🇦', label: 'US + Canada' },
  { key: 'europe', emoji: '🇪🇺', label: 'Europe' },
  { key: 'latin-america', emoji: '🌎', label: 'Latin America' },
  { key: 'africa', emoji: '🌍', label: 'Africa + Middle East' },
  { key: 'asia-oceania', emoji: '🌏', label: 'Asia + Oceania' },
];

/**
 * 🪏 is Unicode 16.0 (Sept 2023) and has no glyph on most systems in use
 * today, where it draws as a tofu box. Sam's replacement, checked in the
 * browser on macOS, iOS and Android before shipping: 🙌.
 */
const GET_INVOLVED_EMOJI = '🙌';

export const REASONS: Option[] = [
  { key: 'learn-my-field', emoji: '💡', label: 'Learn what’s happening in climate' },
  { key: 'build-my-network', emoji: '👥', label: 'Build my network' },
  { key: 'find-a-job', emoji: '🧑‍💻', label: 'Find a job / change careers' },
  { key: 'raise-or-deploy-capital', emoji: '💸', label: 'Raise or deploy capital' },
  { key: 'find-customers-or-partners', emoji: '🤝', label: 'Find customers / collaborators' },
  { key: 'get-involved-locally', emoji: GET_INVOLVED_EMOJI, label: 'Get involved + take local action' },
  { key: SOMETHING_ELSE, emoji: '💬', label: 'Something else…', description: 'Write your own answer!' },
];

/** A stored key back to the words for it, retired answers included. */
export function optionFor(options: Option[], key: string): Option | null {
  return options.find((o) => o.key === key) ?? RETIRED_DESCRIPTORS[key] ?? null;
}

export const COPY = {
  terms: {
    title: 'Welcome to',
    project: 'is a project by',
    agree: 'By using this website, you agree to our',
    ask: 'To help us curate your Climate Week experience, we’d like to get to know you better.',
    go: 'Let’s Go!',
  },
  descriptors: {
    title: 'How would you describe yourself?',
    lead: 'I am a(n)…',
    helper: 'Select up to 3 - you can change these later!',
    otherPlaceholder: 'Tell us in a few words',
  },
  origin: {
    title: 'Where are you based?',
    lead: 'I’m based in…',
  },
  reasons: {
    title: 'What do you want to get out of Climate Week?',
    lead: 'I’m coming to NYCW to…',
    helper: 'Select up to 3 - you can change these later!',
    otherPlaceholder: 'Tell us in a few words',
  },
  done: {
    title: 'You’re all set!',
    lede: 'We hope you get the most out of your Climate Week!',
  },
  /**
   * The account page says "Climate Week" where the question says "NYCW": it is
   * read months later, out of the sign-up flow's context (Sam, Sept 19).
   */
  account: {
    descriptors: 'I am a(n)…',
    origin: 'I’m based in…',
    reasons: 'I’m coming to Climate Week to…',
  },
} as const;

/**
 * Parked Sept 19, not deleted. SMS reminders were cut from the pilot — the step
 * that asked for a number is gone from the flow and no number is ever sent —
 * but the wording is the record of what anyone who answered before then agreed
 * to, character for character as CONTRACT §5 has it, straight apostrophe
 * included. It comes back with the question, if the question comes back.
 */
export const SMS_CONSENT =
  "By adding your number, you agree to get event reminder texts from My Climate Week. Message frequency varies. Msg & data rates may apply. Reply STOP to opt out, HELP for help. We won't share your number.";

/** The longest "Something else" answer the API keeps. */
export const OTHER_MAX = 200;
